import type { ReportTableCell, ReportTableProps } from '@/components';
import type {
  ReportStandardFundForPrivateModel,
  ReportStandardFundsForPrivateModel,
  projectDetailUrlTypeEnum,
} from '@/features/mold/models';
import { isBefore2025 } from '@/features/project/_utils/project';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import {
  BusinessFormLabelModifier,
  CorporateFormLabelModifier,
  FundNecessityTypeLabelModifier,
  OperationFormLabelModifier,
} from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import {
  formatAmountForPdf,
  formatAmountNumberByUnit,
  formatExistenceValue,
  formatNumber,
} from '@/utils';
import type { CSSProperties } from 'react';
import type { z } from 'zod';
import { formatOwnershipRatioFraction } from '../formatter/rs-ledger';
import { centerTextStyle } from '../pdf/pdf-utils';
import { standardFundReportForPrivateValueConverter } from './private-converter';

/// ////////////////////////////////////////
/// Private
/// ////////////////////////////////////////

const headerTextStyleForPrivate: CSSProperties = {
  textAlign: 'center',
  fontSize: '2.5px',
  padding: '1px',
};
const rowTextStyleForPrivate: CSSProperties = {
  fontSize: '2.5px',
  padding: '1px',
};
const amountTextStyleForPrivate: CSSProperties = {
  ...rowTextStyleForPrivate,
  textAlign: 'right',
};

const getAnnualDataHeader = (
  baseFiscalYear: number,
  reportTargetFiscalYears: number[],
): ReportTableProps['header'] =>
  reportTargetFiscalYears.flatMap((year, i) => {
    const isLastOrSecondLastYear =
      i === reportTargetFiscalYears.length - 1 ||
      i === reportTargetFiscalYears.length - 2;

    return [
      {
        type: 'multiple',
        cell: {
          type: 'subset',
          isContainedCellFullHeight: true,
          containedCell: {
            // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
            textWrapperStyle: { height: '100%' },
            type: 'text',
            value: `${t('report-standard-fund-header-total-income', {
              year,
            })}${isLastOrSecondLastYear ? `\n${t('estimate')}` : ''}`,
            textStyle: headerTextStyleForPrivate,
          },
          includedCell: {
            type: 'multiple',
            cell: {
              type: 'flex',
              vertical: true,
              cellRatios: [1, 2],
              flexCells: [
                {
                  type: 'text',
                  value: t('amount-of-funding-from-the-government'),
                  // 文字が小さすぎるためか固定値を入れないと正しく中央に揃わない。
                  textWrapperStyle: { height: '8px' },
                  textStyle: { ...headerTextStyleForPrivate },
                },
                {
                  type: 'multiple',
                  cell: {
                    flexCells: [
                      {
                        ...centerTextStyle,
                        value: t(
                          'report-standard-fund-private-header-granted-amount-by-the-country-initial',
                        ),
                        textStyle: headerTextStyleForPrivate,
                      },
                      {
                        ...centerTextStyle,
                        value: t(
                          'report-standard-fund-private-header-granted-amount-by-the-country-supplementary',
                        ),
                        textStyle: headerTextStyleForPrivate,
                      },
                      {
                        ...centerTextStyle,
                        value: t(
                          'report-standard-fund-private-header-granted-amount-by-the-country-reserve',
                        ),
                        textStyle: headerTextStyleForPrivate,
                      },
                      {
                        ...centerTextStyle,
                        value: t(
                          'report-standard-fund-private-header-granted-amount-by-the-country-accounting-names',
                        ),
                        textStyle: headerTextStyleForPrivate,
                      },
                    ],
                  },
                },
              ],
            },
          },
          cellColRatios: [1, 3],
          cellRowRatios: [1, 3],
        },
        ratio: 8,
      },
      {
        type: 'text',
        value: `${t('report-standard-fund-header-total-expenses', {
          year,
        })}${isLastOrSecondLastYear ? `\n${t('estimate')}` : ''}`,
        textStyle: headerTextStyleForPrivate,
        ratio: 2,
      },
      {
        type: 'text',
        value: `${t(
          'report-standard-fund-header-amount-returned-to-the-national-treasury',
          { year },
        )}${isLastOrSecondLastYear ? `\n${t('estimate')}` : ''}`,
        textStyle: headerTextStyleForPrivate,
        ratio: 2,
      },
      {
        type: 'text',
        value: `${
          isBefore2025({ fiscalYear: baseFiscalYear })
            ? t(
                'report-standard-fund-header-amount-returned-to-the-organization-before-2025',
                { year },
              )
            : t(
                'report-standard-fund-header-amount-returned-to-the-organization',
                { year },
              )
        }${isLastOrSecondLastYear ? `\n${t('estimate')}` : ''}`,
        textStyle: headerTextStyleForPrivate,
        ratio: 2,
      },
      {
        type: 'multiple',
        cell: {
          type: 'subset',
          cellColRatios: [1, 1],
          cellRowRatios: [1, 3],
          isContainedCellFullHeight: true,
          containedCell: {
            type: 'text',
            // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
            textWrapperStyle: { height: '100%' },
            value: `${t(
              'report-standard-fund-header-ending-of-the-fund-balance',
              {
                year,
              },
            )}${isLastOrSecondLastYear ? `\n${t('estimate')}` : ''}`,
            textStyle: headerTextStyleForPrivate,
          },
          includedCell: {
            // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
            textWrapperStyle: { height: '100%' },
            value: t(
              'report-standard-fund-private-header-national-equivalent-of-the-balance',
              {
                year,
              },
            ),
            textStyle: headerTextStyleForPrivate,
          },
        },
        ratio: 4,
      },
    ];
  });

export const getStandardFundReportForPrivateHeader = (
  baseFiscalYear: number,
  reportTargetFiscalYears: number[],
): ReportTableProps['header'] => {
  const annualDataHeader = getAnnualDataHeader(
    baseFiscalYear,
    reportTargetFiscalYears,
  );

  return [
    {
      type: 'text',
      value: t('report-standard-fund-header-fund-sheet-number'),
      textStyle: headerTextStyleForPrivate,
      ratio: 2,
    },
    {
      type: 'text',
      value: t('ministry'),
      textStyle: headerTextStyleForPrivate,
      ratio: 2,
    },
    {
      type: 'text',
      value: t('additional-organization-in-charge'),
      textStyle: headerTextStyleForPrivate,
      ratio: 2,
    },
    {
      type: 'text',
      value: t('fund-business-name'),
      textStyle: headerTextStyleForPrivate,
      ratio: 4,
    },
    {
      type: 'text',
      value: t('fund-name'),
      textStyle: headerTextStyleForPrivate,
      ratio: 4,
    },
    {
      type: 'text',
      value: t('fund-owner'),
      textStyle: headerTextStyleForPrivate,
      ratio: 4,
    },
    {
      type: 'text',
      value: t('corporate-form'),
      textStyle: headerTextStyleForPrivate,
      ratio: 3,
    },
    {
      type: 'text',
      value: t('fiscal-year-of-creation'),
      textStyle: headerTextStyleForPrivate,
      ratio: 1.5,
    },
    {
      type: 'text',
      value: t('fund-scheduled-end-period'),
      textStyle: headerTextStyleForPrivate,
      ratio: 2,
    },
    {
      type: 'text',
      value: t('end-date-of-new-application'),
      textStyle: headerTextStyleForPrivate,
      ratio: 2,
    },
    {
      type: 'text',
      value: t('operation-form'),
      textStyle: headerTextStyleForPrivate,
      ratio: 3,
    },
    {
      type: 'text',
      value: t('business-form'),
      textStyle: headerTextStyleForPrivate,
      ratio: 3,
    },
    {
      type: 'text',
      value: t('fund-necessity-type'),
      textStyle: headerTextStyleForPrivate,
      ratio: 2,
    },
    {
      type: 'text',
      value: t('report-standard-fund-private-header-has-legal-bases'),
      textStyle: headerTextStyleForPrivate,
      ratio: 1.5,
    },
    {
      type: 'text',
      value: t('expenditure-budget-name'),
      textStyle: headerTextStyleForPrivate,
      ratio: 3,
    },
    {
      type: 'text',
      value: t(
        'report-standard-fund-private-header-have-applied-law-of-appropriateness-of-subsidies',
      ),
      textStyle: headerTextStyleForPrivate,
      ratio: 1.5,
    },
    {
      type: 'text',
      value: t('report-standard-fund-header-ending-of-the-fund-balance', {
        ...(reportTargetFiscalYears[0] && {
          year: reportTargetFiscalYears[0] - 1,
        }),
      }),
      textStyle: headerTextStyleForPrivate,
      ratio: 2.125,
    },
    ...annualDataHeader,
    {
      type: 'text',
      value: t('ownership-ratio'),
      textStyle: headerTextStyleForPrivate,
      ratio: 1,
    },
  ];
};

const getRowValueText = (
  key:
    | Exclude<
        keyof z.infer<typeof ReportStandardFundForPrivateModel>,
        'ownershipRatio'
      >
    | 'grantedAmountByTheCountryAccountingNames'
    | 'endingOfTheFundBalance',
  value: string | number | string[] | null | undefined,
) => {
  if (Array.isArray(value)) {
    switch (key) {
      case 'operationForms':
        return value.map((d) => OperationFormLabelModifier[d]).join(', ');
      case 'businessForms':
        return value.map((d) => BusinessFormLabelModifier[d]).join(', ');
      default:
        return value.join(', ');
    }
  }

  if (key === 'necessityType') {
    return value ? FundNecessityTypeLabelModifier[value] : '';
  }
  if (key === 'endingOfTheFundBalance') {
    if (typeof value !== 'number') {
      return '';
    }
    const thousandAmount = formatAmountNumberByUnit(value, 'thousand');
    return formatNumber(thousandAmount);
  }

  return value === null ? '' : value;
};

const getCorporateFormText = (
  corporateForm: z.infer<
    typeof ReportStandardFundForPrivateModel
  >['corporateForm'],
  optionalCorporateForm: z.infer<
    typeof ReportStandardFundForPrivateModel
  >['optionalCorporateForm'],
) => {
  if (corporateForm === 'others' && optionalCorporateForm) {
    return optionalCorporateForm;
  }
  return corporateForm ? CorporateFormLabelModifier[corporateForm] : '';
};

const getStandardFundReportForPrivateRow = ({
  data,
  reportTargetFiscalYears,
  isDebugMode,
  projectDetailUrlType,
}: {
  data: z.infer<typeof ReportStandardFundForPrivateModel>;
  reportTargetFiscalYears: number[];
  isDebugMode: boolean;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
}): ReportTableCell[] => {
  return [
    {
      type: 'text',
      value: getRowValueText(
        'fundSheetNumber',
        `${data.fundSheetNumber.toString()}${
          data.fundSheetBranchNumber ? `-${data.fundSheetBranchNumber}` : ''
        }`,
      ),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: getRowValueText('ministryName', data.ministryName),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: getRowValueText(
        'additionalJurisdictionalMinistries',
        data.additionalJurisdictionalMinistries,
      ),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: getRowValueText('fundBusinessName', data.fundBusinessName),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'link',
      href: getProjectDetailPageUrl({
        isPS: projectDetailUrlType === 'public',
        projectId: data.id,
      }),
      value: String(getRowValueText('projectName', data.projectName)),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: getRowValueText('fundOwner', data.fundOwner),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: getCorporateFormText(
        data.corporateForm,
        data.optionalCorporateForm,
      ),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: getRowValueText('fiscalYearOfCreation', data.fiscalYearOfCreation),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: getRowValueText('endDate', data.endDate),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: getRowValueText(
        'endDateOfNewApplicationReceiving',
        data.endDateOfNewApplicationReceiving,
      ),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: getRowValueText('operationForms', data.operationForms),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: getRowValueText('businessForms', data.businessForms),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: getRowValueText('necessityType', data.necessityType),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: formatExistenceValue(data.hasLegalBases),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: getRowValueText('expenditurePurposes', data.expenditurePurposes),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: formatExistenceValue(
        data.haveAppliedLawOfAppropriatenessOfSubsidies,
      ),
      textStyle: rowTextStyleForPrivate,
    },
    {
      type: 'text',
      value: formatAmountForPdf(
        data.annualData.find((d) => d.fiscalYear === reportTargetFiscalYears[0])
          ?.beginningOfTheFundBalance,
        {
          rawValue: data.annualData.find(
            (d) => d.fiscalYear === reportTargetFiscalYears[0],
          )?.rawBeginningOfTheFundBalance,
        },
      ),
      textStyle: amountTextStyleForPrivate,
    },
    ...reportTargetFiscalYears.flatMap((year) => {
      const yearData = data.annualData.find((d) => d.fiscalYear === year);
      const lastYearData = data.annualData.find(
        (d) => d.fiscalYear === year - 1,
      );

      const values = standardFundReportForPrivateValueConverter({
        lastYearBeginningOfTheFundBalance:
          lastYearData?.beginningOfTheFundBalance,
        lastYearRawBeginningOfTheFundBalance:
          lastYearData?.rawBeginningOfTheFundBalance,
        annualData: yearData,
        type: 'pdf',
        isDebugMode,
      });
      return [
        {
          type: 'multiple',
          cell: {
            type: 'flex',
            flexCells: [
              {
                ...centerTextStyle,
                value: values.totalIncome,
                textStyle: amountTextStyleForPrivate,
              },
              {
                type: 'multiple',
                cell: {
                  type: 'flex',
                  flexCells: [
                    {
                      ...centerTextStyle,
                      value: formatAmountForPdf(
                        yearData?.grantedAmountByTheCountryInitial,
                        {
                          rawValue:
                            yearData?.rawGrantedAmountByTheCountryInitial,
                        },
                      ),
                      textStyle: amountTextStyleForPrivate,
                    },
                    {
                      ...centerTextStyle,
                      value: formatAmountForPdf(
                        yearData?.grantedAmountByTheCountrySupplementary,
                        {
                          rawValue:
                            yearData?.rawGrantedAmountByTheCountrySupplementary,
                        },
                      ),
                      textStyle: amountTextStyleForPrivate,
                    },
                    {
                      ...centerTextStyle,
                      value: formatAmountForPdf(
                        yearData?.grantedAmountByTheCountryReserve,
                        {
                          rawValue:
                            yearData?.rawGrantedAmountByTheCountryReserve,
                        },
                      ),
                      textStyle: amountTextStyleForPrivate,
                    },
                    {
                      ...centerTextStyle,
                      value: getRowValueText(
                        'grantedAmountByTheCountryAccountingNames',
                        yearData?.grantedAmountByTheCountryAccountingNames,
                      ),
                      textStyle: rowTextStyleForPrivate,
                    },
                  ],
                },
              },
            ],
            cellRatios: [1, 3],
          },
        },
        {
          type: 'text',
          value: values.totalExpenses,
          textStyle: amountTextStyleForPrivate,
        },
        {
          type: 'text',
          value: formatAmountForPdf(
            yearData?.amountReturnedToTheNationalTreasury,
            {
              rawValue: yearData?.rawAmountReturnedToTheNationalTreasury,
            },
          ),
          textStyle: amountTextStyleForPrivate,
        },
        {
          type: 'text',
          value: formatAmountForPdf(yearData?.amountReturnedToTheOrganization, {
            rawValue: yearData?.rawAmountReturnedToTheOrganization,
          }),
          textStyle: amountTextStyleForPrivate,
        },
        {
          type: 'multiple',
          cell: {
            flexCells: [
              {
                ...centerTextStyle,
                value: values.endingOfTheFundBalance,
                textStyle: amountTextStyleForPrivate,
              },
              {
                ...centerTextStyle,
                value: formatAmountForPdf(
                  yearData?.nationalEquivalentOfTheBalance,
                  {
                    rawValue: yearData?.rawNationalEquivalentOfTheBalance,
                  },
                ),
                textStyle: amountTextStyleForPrivate,
              },
            ],
          },
        },
      ] as ReportTableCell[];
    }),
    {
      type: 'text',
      value: formatOwnershipRatioFraction(
        data.ratioNumerator,
        data.ratioDenominator,
      ),
      textStyle: rowTextStyleForPrivate,
    },
  ];
};

export const getStandardFundReportForPrivatePdfRowData = ({
  data,
  reportTargetFiscalYears,
  isDebugMode,
  projectDetailUrlType,
}: {
  data: z.infer<typeof ReportStandardFundsForPrivateModel.shape.data>;
  reportTargetFiscalYears: number[];
  isDebugMode: boolean;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
}): ReportTableProps['data'] => {
  return data.map((d) => ({
    row: getStandardFundReportForPrivateRow({
      data: d,
      reportTargetFiscalYears,
      isDebugMode,
      projectDetailUrlType,
    }),
  }));
};

const getAnnualDataFooter = (
  reportTargetFiscalYears: number[],
  summaryData: z.infer<
    typeof ReportStandardFundsForPrivateModel.shape.summaryData
  >,
): ReportTableCell[] =>
  reportTargetFiscalYears.flatMap((year) => {
    const yearData = summaryData.find((d) => d.fiscalYear === year);
    return [
      {
        type: 'multiple',
        cell: {
          type: 'flex',
          flexCells: [
            {
              ...centerTextStyle,
              value: formatAmountForPdf(yearData?.totalIncomeSummary),
              textStyle: amountTextStyleForPrivate,
            },
            {
              type: 'multiple',
              cell: {
                type: 'flex',
                flexCells: [
                  {
                    ...centerTextStyle,
                    value: formatAmountForPdf(
                      yearData?.grantedAmountByTheCountryInitialSummary,
                    ),
                    textStyle: amountTextStyleForPrivate,
                  },
                  {
                    ...centerTextStyle,
                    value: formatAmountForPdf(
                      yearData?.grantedAmountByTheCountrySupplementarySummary,
                    ),
                    textStyle: amountTextStyleForPrivate,
                  },
                  {
                    ...centerTextStyle,
                    value: formatAmountForPdf(
                      yearData?.grantedAmountByTheCountryReserveSummary,
                    ),
                    textStyle: amountTextStyleForPrivate,
                  },
                  {
                    ...centerTextStyle,
                    value: '',
                    textStyle: rowTextStyleForPrivate,
                  },
                ],
              },
            },
          ],
          cellRatios: [1, 3],
        },
      },
      {
        type: 'text',
        value: formatAmountForPdf(yearData?.totalExpensesSummary),
        textStyle: amountTextStyleForPrivate,
      },
      {
        type: 'text',
        value: formatAmountForPdf(
          yearData?.amountReturnedToTheNationalTreasurySummary,
        ),
        textStyle: amountTextStyleForPrivate,
      },
      {
        type: 'text',
        value: formatAmountForPdf(
          yearData?.amountReturnedToTheOrganizationSummary,
        ),
        textStyle: amountTextStyleForPrivate,
      },
      {
        type: 'multiple',
        cell: {
          type: 'flex',
          flexCells: [
            {
              ...centerTextStyle,
              value: formatAmountForPdf(
                yearData?.endingOfTheFundBalanceSummary,
              ),
              textStyle: amountTextStyleForPrivate,
            },
            {
              ...centerTextStyle,
              value: formatAmountForPdf(
                yearData?.nationalEquivalentOfTheBalanceSummary,
              ),
              textStyle: amountTextStyleForPrivate,
            },
          ],
        },
      },
    ] as ReportTableCell[];
  });

export const getStandardFundReportForPrivateFooter = ({
  reportTargetFiscalYears,
  summaryData,
}: {
  reportTargetFiscalYears: number[];
  summaryData: z.infer<
    typeof ReportStandardFundsForPrivateModel.shape.summaryData
  >;
}): ReportTableProps['footer'] => {
  const annualDataFooter = getAnnualDataFooter(
    reportTargetFiscalYears,
    summaryData,
  );
  const reportTargetFirstYearSummary = summaryData.find(
    (d) => d.fiscalYear === reportTargetFiscalYears[0],
  );
  return [
    {
      row: [
        {
          type: 'text',
          value: t('total'),
          colSpan: 16,
          align: 'center',
          textStyle: rowTextStyleForPrivate,
        },
        {
          type: 'text',
          value: formatAmountForPdf(
            reportTargetFirstYearSummary?.beginningOfTheFundBalanceSummary,
          ),
          textStyle: amountTextStyleForPrivate,
        },
        ...annualDataFooter,
        {
          type: 'text',
          value: '',
          textStyle: rowTextStyleForPrivate,
        },
      ],
      rowStyle: {
        height: 10,
      },
    },
  ];
};
